import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Paper,
  Stack,
  alpha,
  useTheme,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Analytics,
  Print as PrintIcon,
  ErrorOutline,
} from "@mui/icons-material";
import SummaryTable from "./SummaryTable";
import VefTable from "./VefTable";
import fakeVefData from "./fakeData";
import { renderVefV2 } from "../../print-v2";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const VesselExperienceFactor = () => {
  const { t } = useTranslation("vef");
  const theme = useTheme();
  const [voyages, setVoyages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      const enriched = fakeVefData.map((v) => {
        const ratio = v.shoreBL ? v.shipLoaded / v.shoreBL : 0;
        const qualifying =
          ratio <= 1.002 &&
          ratio >= 0.998 &&
          v.basis === "Shore" &&
          v.rejected === "No";

        return {
          ...v,
          ratio: ratio.toFixed(4),
          qualifying,
        };
      });

      const sorted = enriched.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      setVoyages(sorted);
      setLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  // Beräkningar för de 20 senaste
  const latest20 = voyages.slice(0, 20);
  const totalShipAll = latest20.reduce((sum, v) => sum + v.shipLoaded, 0);
  const totalShoreAll = latest20.reduce((sum, v) => sum + v.shoreBL, 0);
  const overallRatio = totalShoreAll ? totalShipAll / totalShoreAll : 0;

  const qualified = latest20.filter((v) => v.qualifying);
  const totalShipQualified = qualified.reduce(
    (sum, v) => sum + v.shipLoaded,
    0
  );
  const totalShoreQualified = qualified.reduce((sum, v) => sum + v.shoreBL, 0);
  const vef = totalShoreQualified
    ? totalShipQualified / totalShoreQualified
    : 0;

  const meanShipRatioLoaded = vef * 1.003;
  const meanBLRatioLoaded = vef * 0.997;

  const totals = {
    totalShipAll,
    totalShoreAll,
    overallRatio,
    totalShipQualified,
    totalShoreQualified,
    vef,
  };

  const onPrint = () =>
    renderVefV2({
      voyages: latest20,
      totals,
      generatedAt: dayjs().format("YYYY-MM-DD HH:mm"),
      t,
    });

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress thickness={2} />
      </Box>
    );

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", pb: 8, px: 2 }}>
      {/* 1. Header Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "flex-end" }}
        sx={{ mb: 4, mt: 2 }}
        spacing={2}
      >
        <Box>
          <Typography
            variant="h3"
            fontWeight="950"
            sx={{
              letterSpacing: -1,
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Analytics sx={{ fontSize: 45, color: "primary.main" }} />
            {t("title")}
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{ fontSize: "1.1rem" }}
          >
            Vessel Experience Factor based on the 20 latest qualifying voyages.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<PrintIcon />}
          onClick={onPrint}
          sx={{ borderRadius: 3, px: 4, fontWeight: 800, height: 50 }}
        >
          Print VEF Report
        </Button>
      </Stack>

      {/* 2. Status Alert */}
      <Alert
        severity="info"
        icon={<ErrorOutline />}
        sx={{
          mb: 4,
          borderRadius: 4,
          fontWeight: 600,
          bgcolor: alpha(theme.palette.info.main, 0.05),
          border: "1px solid",
          borderColor: alpha(theme.palette.info.main, 0.1),
        }}
      >
        Currently using simulated historical data. The factor will update
        automatically as new voyages are completed.
      </Alert>

      <Stack spacing={6}>
        {/* 3. Summary & Calculation (Nu med allt fokus) */}
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight="800"
            color="text.disabled"
            sx={{ mb: 2, px: 1, letterSpacing: 1 }}
          >
            CALCULATION SUMMARY
          </Typography>
          <SummaryTable
            totalShipAll={totalShipAll}
            totalShoreAll={totalShoreAll}
            overallRatio={overallRatio}
            totalShipQualified={totalShipQualified}
            totalShoreQualified={totalShoreQualified}
            vef={vef}
            meanShipRatioLoaded={meanShipRatioLoaded}
            meanBLRatioLoaded={meanBLRatioLoaded}
          />
        </Box>

        {/* 4. Historical Log */}
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight="800"
            color="text.disabled"
            sx={{ mb: 2, px: 1, letterSpacing: 1 }}
          >
            VOYAGE LOG (LAST 20)
          </Typography>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              boxShadow: `0 4px 20px -5px ${alpha(
                theme.palette.common.black,
                0.05
              )}`,
            }}
          >
            <VefTable voyages={voyages} />
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};

export default VesselExperienceFactor;
